import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createPaginationContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

import actions from '../../actions/FavoritesActions';
import { copy, followingTypes, modals } from '../../constants';

import FollowingHeader from './FollowingHeader';
import FollowingCarousel from './FollowingCarousel';
import LoadMore from '../Tiles/LoadMore';

import get from 'lodash.get';

import style from './styles/followingContainer.scss';
/* eslint no-unused-vars:0 */
import trackingHelpers from '../../helpers/trackingHelpers';

const ITEMS_TO_LOAD = 12;
const SPACE = ' ';
const { SEARCH } = followingTypes;

class FollowingRefetchWrapper extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            favoritesItems: {},
            itemsShown: ITEMS_TO_LOAD,
        };

        this.edges = null;

        this.loadMoreItems = this.loadMoreItems.bind(this);
    }

    loadMoreItems() {
        if (this.props.relay.hasMore()) {
            this.props.setPartialPageLoading(true);
            this.props.relay.loadMore(ITEMS_TO_LOAD, () => {
                this.props.setPartialPageLoading(false);
                this.setState({
                    itemsShown: this.state.itemsShown + ITEMS_TO_LOAD,
                });
            });
        }
    }

    render() {
        const { itemsShown } = this.state;
        const { isLoadingPartialPage, launchModal, relay, user, deviceType } = this.props;
        const edges = get(user, 'favorites.edges') || [];
        const totalResults = get(user, 'favorites.totalResults');

        if (!totalResults) {
            return null;
        }

        return (
            <Fragment>
                <div className={style.container}>
                    <div className={style.listContainer}>
                        {edges.map(({ node }, index) => {
                            const { page, parameters, type, name, serviceId } = node;
                            const items = get(node, 'items.edges') || [];
                            const sortByNewest = `${page.indexOf('?') > -1 ? '&' : '?'}sort=newest`;
                            const pageUrl = `${page}${type === SEARCH ? sortByNewest : ''}`;
                            let favoriteEl;
                            let pageLink = true;

                            if (items.length === 0) {
                                favoriteEl = (
                                    <div className={style.sorryMessage}>
                                        Sorry, items are not available.
                                    </div>
                                );
                                pageLink = false;
                            } else {
                                favoriteEl = (
                                    <FollowingCarousel
                                        items={items}
                                        id={serviceId}
                                        deviceType={deviceType}
                                    />
                                );
                            }
                            return (
                                <div key={serviceId} data-tn={`following-container-${index}`}>
                                    <FollowingHeader
                                        removeFollowing={() => launchModal(modals.REMOVE_FOLLOWING)}
                                        type={type}
                                        page={pageUrl}
                                        pageLink={pageLink}
                                        parameters={parameters}
                                        name={decodeURIComponent(name)}
                                        item={node}
                                    />
                                    {favoriteEl}
                                </div>
                            );
                        })}
                    </div>
                    {relay.hasMore() && (
                        <LoadMore
                            itemsShown={itemsShown}
                            isLoading={isLoadingPartialPage}
                            onClick={this.loadMoreItems}
                            totalItems={totalResults}
                            itemsToCut={ITEMS_TO_LOAD}
                        />
                    )}
                </div>
                <div className={style.footer}>
                    <span>{copy.following.PREFERENCES_TEXT}</span>
                    {SPACE}
                    <a
                        className={style.preferencesLink}
                        href="/my/account/email/"
                        data-tn="communication-preferences"
                    >
                        {copy.following.PREFERENCES_TITLE}
                    </a>
                </div>
            </Fragment>
        );
    }
}

FollowingRefetchWrapper.propTypes = {
    isLoadingFullPage: PropTypes.bool,
    isLoadingPartialPage: PropTypes.bool,
    launchModal: PropTypes.func,
    setFullPageLoading: PropTypes.func,
    relay: PropTypes.object,
    setNumItems: PropTypes.func,
    setPartialPageLoading: PropTypes.func,
    setTotalResults: PropTypes.func,
    user: PropTypes.object,
    userId: PropTypes.string,
    deviceType: PropTypes.string,
};

const mapStateToProps = state => {
    return {
        isLoadingPartialPage: state.favorites.uiState.isLoadingPartialPage,
        totalResults: state.favorites.uiState.totalResults,
        deviceType: state.favorites.uiState.deviceType,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setNumItems: first => {
            dispatch(actions.setNumItems({ first }));
        },
        setFullPageLoading: isLoadingFullPage => {
            dispatch(actions.setFullPageLoading({ isLoadingFullPage }));
        },
        setPartialPageLoading: isLoadingPartialPage => {
            dispatch(actions.setPartialPageLoading({ isLoadingPartialPage }));
        },
        setTotalResults: totalResults => {
            dispatch(actions.setTotalResults({ totalResults }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(FollowingRefetchWrapper);
FollowingRefetchWrapper = createPaginationContainer(
    ReduxWrapped,
    {
        user: graphql`
            fragment FollowingRefetchWrapper_user on User {
                favorites(first: 12, after: $after, types: [SEARCH, SELLER, DESIGNER])
                    @connection(key: "FollowingRefetchWrapper_favorites") {
                    totalResults
                    edges {
                        cursor
                        node {
                            serviceId
                            page
                            name
                            query
                            type
                            parameters {
                                key
                                type
                                values
                            }
                            items(first: 12, page: 0) {
                                edges {
                                    node {
                                        serviceId
                                        title
                                        pdpURL
                                        ...trackingHelpers_item @relay(mask: false)
                                        photos {
                                            versions {
                                                webPath
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    {
        direction: 'forward',
        getConnectionFromProps(props) {
            return props.user && props.user.favorites;
        },
        getFragmentVariables(prevVars, first) {
            return {
                ...prevVars,
                first,
            };
        },
        getVariables(props, { count, cursor }) {
            return {
                first: count,
                after: cursor,
                userId: props.userId,
            };
        },
        query: graphql`
            query FollowingRefetchWrapperRefetchQuery($after: String, $userId: String!) {
                viewer {
                    user(userId: $userId) {
                        ...FollowingRefetchWrapper_user
                    }
                }
            }
        `,
    }
);

export default FollowingRefetchWrapper;
